import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import api from '@/api';
import { isErrorCode } from '@/api/errors';
import { TopicContent } from '@/courses/topic/TopicContent';
import { useCourseContext } from '~/contexts/courseContext';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute('/_app/_course/topic/$topicSlug')({
  component: TopicScreen,
});

function TopicScreen() {
  const { topicSlug } = Route.useParams();
  const { setSelectedLessonSlug, setExpandedLessons, setSelectedTopicSlug } =
    useCourseContext();
  const { me, isStudent } = useCurrentUser();
  const navigate = useNavigate();
  const language = me?.language ?? 'EN';

  // `topic_by_slug` is the RESOLVER and is deliberately ungated: it carries the
  // lesson and course ids plus `course_slug`, which is what the 403 branch below
  // needs. Reading them off `topicDetail` instead — the access-gated body read —
  // left a refused student on a permanently blank page, because web has no
  // global 403 interceptor and the redirect target was underivable.
  const { data: resolved } = api.topicBySlug.useQuery({
    path: { slug: topicSlug },
    query: { language },
  });

  const topicId = resolved?.id;
  const lessonId = resolved?.lesson_id;
  const courseId = resolved?.course_id;
  const lessonSlug = resolved?.lesson_slug ?? '';

  const { data: topicDetail, error: topicDetailError } =
    api.topicDetail.useQuery(
      { path: { topic_id: topicId ?? 0 } },
      { enabled: topicId != null },
    );

  const { data: lessonDetail } = api.lessonDetail.useQuery(
    { path: { lesson_id: lessonId ?? 0 } },
    { enabled: lessonId != null },
  );

  const { data: topicsData } = api.lessonTopics.useQuery(
    { path: { lesson_id: lessonId ?? 0 }, query: { limit: 50 } },
    { enabled: lessonId != null },
  );

  const { data: courseLessons } = api.courseLessons.useQuery(
    { path: { course_id: courseId ?? 0 }, query: { limit: 50 } },
    { enabled: courseId != null },
  );

  const { data: courseDetail } = api.courseDetail.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: viewerState } = api.courseViewerState.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: courseProgress } = api.courseProgress.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null && isStudent },
  );

  const { mutateAsync: markComplete } = api.topicComplete.useMutation();
  const { mutateAsync: submitTopicQuiz } = api.submitTopicQuiz.useMutation();

  // The only wire source for the inline topic-quiz panel's copy: legacy's
  // `TopicQuiz.php:28-56` picks between the graded and the grading panel, and
  // every string in both is a `wp_options` row the API now resolves per language.
  const { data: topicQuizResult, isLoading: topicQuizResultLoading } =
    api.topicQuizResult.useQuery(
      { path: { topic_id: topicId ?? 0 } },
      { enabled: topicId != null },
    );

  useEffect(() => {
    if (!lessonSlug) return;
    setSelectedLessonSlug(lessonSlug);
    setExpandedLessons((prev) =>
      prev.includes(lessonSlug) ? prev : [...prev, lessonSlug],
    );
    setSelectedTopicSlug(topicSlug);
  }, [
    lessonSlug,
    topicSlug,
    setSelectedLessonSlug,
    setExpandedLessons,
    setSelectedTopicSlug,
  ]);

  const courseSlug = resolved?.course_slug;

  // `topic.php`'s 302, measured: `/topic/gods-presence-is-well-known/` -> 302 ->
  // `/courses/god-has-spoken/` anonymously and for a logged-in student with no
  // grant. `topic_detail` is the gated read here (it carries the 3,414 topic
  // bodies); the state read is the belt-and-braces half and covers the case where
  // the two disagree.
  const denied =
    viewerState?.has_access === false ||
    isErrorCode(topicDetailError, 'course_access_required');
  if (denied && courseSlug) {
    return (
      <Navigate to="/courses/$courseSlug/" params={{ courseSlug }} replace />
    );
  }

  const waitingForQuizState = topicId != null && topicQuizResultLoading;

  if (!topicDetail || !lessonDetail || waitingForQuizState) {
    return null;
  }

  const lessonProgress = courseProgress
    ? courseProgress[String(lessonId)]
    : undefined;

  const lessonProgressPct =
    lessonProgress && lessonProgress.topics_total > 0
      ? Math.round(
          (lessonProgress.topics_completed / lessonProgress.topics_total) * 100,
        )
      : 0;

  // Set membership, not `topic.order <= topics_completed`. That comparison put a
  // COURSE-scoped order against a LESSON-scoped count: measured 2026-08-17, 520
  // of 520 lessons have a first topic whose `order` is not 1 (min 2, max 164), so
  // the old badge was wrong for every lesson in the corpus.
  const isCompleted =
    lessonProgress?.completed_topic_ids.includes(topicDetail.id) ?? false;

  const handleSubmitQuiz = async (
    answers: Record<string, string | string[] | boolean[]>,
  ) => {
    await submitTopicQuiz({
      path: { topic_id: topicId ?? 0 },
      body: { answers: answers as any },
    });
  };

  // Navigation only. `submit_quiz_response` (`grades/service.py:790-791`) already
  // marks the topic complete for a topic-scoped submit, so calling
  // `topic_complete` after a quiz submit was a second, redundant write.
  const advance = async () => {
    const topics = topicsData?.data ?? [];
    const nextTopic = topics.find((t) => t.order === topicDetail.order + 1);

    if (nextTopic) {
      navigate({
        to: '/topic/$topicSlug/',
        params: { topicSlug: nextTopic.slug },
      });
      return;
    }

    // `has_final_quiz` replaces the `lessonQuestions.length > 0` probe this page
    // used to fire on every topic. That operation is now quiz-locked, and on the
    // LAST topic the topics are by definition not yet all complete — so the probe
    // would 403, read false, and route the student past the quiz to the next
    // lesson.
    if (lessonDetail.has_final_quiz && lessonSlug) {
      navigate({
        to: '/lessons/$lessonSlug/quiz/',
        params: { lessonSlug },
      });
      return;
    }

    const nextLesson = (courseLessons?.data ?? []).find(
      (l) => l.order === lessonDetail.order + 1,
    );
    if (nextLesson) {
      navigate({
        to: '/lessons/$lessonSlug/',
        params: { lessonSlug: nextLesson.slug },
      });
      return;
    }

    if (courseSlug) {
      navigate({
        to: '/courses/$courseSlug/',
        params: { courseSlug },
      });
    }
  };

  const handleContinue = async () => {
    await markComplete({ path: { topic_id: topicId ?? 0 } });
    await advance();
  };

  return (
    <TopicContent
      courseTitle={courseDetail?.title ?? ''}
      courseSlug={courseSlug ?? ''}
      lessonId={lessonId}
      lessonTitle={lessonDetail.title}
      lessonSlug={lessonSlug}
      topicTitle={topicDetail.title}
      topicSlug={topicSlug}
      htmlContent={topicDetail.content}
      lessonNumber={lessonDetail.index + 1}
      topicNumber={topicDetail.index + 1}
      thoughtQuestions={topicDetail.questions}
      quizResult={topicQuizResult}
      memberId={me?.id?.toString()}
      isStudent={isStudent}
      lessonProgress={lessonProgressPct}
      isCompleted={isCompleted}
      onContinue={handleContinue}
      onQuizSubmitted={advance}
      onSubmitQuiz={handleSubmitQuiz}
    />
  );
}
